import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Award, RotateCcw, BookOpen } from 'lucide-react';
import { topicsApi, Topic, Question } from '../api/topics';
import { useModuleCompletion } from '../hooks/useModuleCompletion';

export const SectionQuizPage: React.FC = () => {
  const { slug, sectionIndex } = useParams<{ slug: string; sectionIndex: string }>();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [autoRedirectCountdown, setAutoRedirectCountdown] = useState<number | null>(null);
  const { markModuleComplete } = useModuleCompletion();

  const currentIndex = parseInt(sectionIndex || '0', 10);

  const handleContinueToNext = useCallback(() => {
    if (!topic || !topic.sections) return;
    
    // Stop auto-redirect countdown
    setAutoRedirectCountdown(null);
    
    const totalSections = topic.sections.length;
    const nextSectionIndex = currentIndex + 1;
    
    if (nextSectionIndex < totalSections) {
      // Go to next section
      navigate(`/topics/${slug}/section/${nextSectionIndex}`);
    } else {
      // Go back to topic overview (last section completed)
      navigate(`/topics/${slug}`);
    }
  }, [topic, currentIndex, navigate, slug]);

  useEffect(() => {
    const fetchTopic = async () => {
      if (!slug) return;
      
      try {
        const data = await topicsApi.getBySlug(slug);
        setTopic(data);
      } catch (error) {
        console.error('Error fetching topic:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
  }, [slug]);

  // Auto-redirect countdown effect
  useEffect(() => {
    if (autoRedirectCountdown !== null && autoRedirectCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoRedirectCountdown(autoRedirectCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoRedirectCountdown === 0) {
      handleContinueToNext();
    }
  }, [autoRedirectCountdown, handleContinueToNext]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!topic || !topic.sections || !topic.sections[currentIndex]) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Module niet gevonden</h1>
        <Link to="/topics" className="text-blue-600 hover:underline">
          Terug naar delen
        </Link>
      </div>
    );
  }

  const currentSection = topic.sections[currentIndex];
  const questions = currentSection.questions || [];

  if (questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Geen vragen beschikbaar</h1>
          <p className="text-gray-600 mb-6">
            Er zijn momenteel geen vragen beschikbaar voor deze module.
          </p>
          <Link to={`/topics/${slug}/section/${currentIndex}`}>
            <button className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors">
              Terug naar module
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  
  // Convert question choices to options array
  const currentOptions = currentQuestion ? [
    { label: 'A', text: currentQuestion.choice_a },
    { label: 'B', text: currentQuestion.choice_b },
    { label: 'C', text: currentQuestion.choice_c },
    { label: 'D', text: currentQuestion.choice_d },
  ] : [];

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: answer
    });
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Mark current module as complete
      if (topic) {
        markModuleComplete(topic.id, currentIndex);
      }
      setShowResults(true);
      
      // Start auto-redirect countdown
      setAutoRedirectCountdown(5);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowResults(false);
    setAutoRedirectCountdown(null);
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((question, index) => {
      if (selectedAnswers[index] === question.answer) {
        correct++;
      }
    });
    return {
      correct,
      total: totalQuestions,
      percentage: Math.round((correct / totalQuestions) * 100)
    };
  };

  if (showResults) {
    const score = calculateScore();
    const isPassed = score.percentage >= 70;

    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link 
          to={`/topics/${slug}/section/${currentIndex}`}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug naar module
        </Link>

        <div className="bg-white border border-gray-200 rounded-lg p-8">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
            isPassed ? 'bg-green-100' : 'bg-orange-100'
          }`}>
            <Award className={`w-10 h-10 ${isPassed ? 'text-green-600' : 'text-orange-600'}`} />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            {isPassed ? 'Goed gedaan!' : 'Blijf oefenen!'}
          </h1>
          <p className="text-xl text-gray-600 mb-4 text-center">
            Je hebt {score.correct} van de {score.total} vragen goed beantwoord
          </p>

          <div className="text-center mb-8">
            <div className="text-6xl font-bold mb-2" style={{ color: isPassed ? '#16a34a' : '#ea580c' }}>
              {score.percentage}%
            </div>
            
            {/* Auto-redirect countdown */}
            {autoRedirectCountdown !== null && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-700 text-sm">
                  Automatisch doorgaan naar {topic && currentIndex + 1 < (topic.sections?.length || 0) 
                    ? 'volgende module' 
                    : 'overzicht'} over {autoRedirectCountdown} seconden...
                </p>
              </div>
            )}
          </div>

          {/* Answer Review */}
          <div className="space-y-4 mb-8">
            {questions.map((question, index) => {
              const userAnswer = selectedAnswers[index];
              const isCorrect = userAnswer === question.answer;
              const correctAnswerText = question[`choice_${question.answer.toLowerCase()}` as keyof Question] as string;
              
              return (
                <div key={question.id} className={`p-4 rounded-lg border-2 ${
                  isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}>
                  <div className="flex items-start space-x-3">
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900 mb-2">
                        Vraag {index + 1}: {question.question}
                      </p>
                      {!isCorrect && (
                        <div className="text-sm">
                          <p className="text-red-700 mb-1">
                            <span className="font-medium">Jouw antwoord:</span> {userAnswer}
                          </p>
                          <p className="text-green-700">
                            <span className="font-medium">Correct antwoord:</span> {question.answer} - {correctAnswerText}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handleRestart}
              className="inline-flex items-center px-6 py-3 rounded-md font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors border border-purple-200"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Opnieuw proberen
            </button>
            
            {/* Auto-navigate to next section or topic overview */}
            <button
              onClick={handleContinueToNext}
              className="inline-flex items-center px-6 py-3 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              {topic && currentIndex + 1 < (topic.sections?.length || 0) 
                ? 'Volgende module' 
                : 'Terug naar overzicht'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link 
        to={`/topics/${slug}/section/${currentIndex}`}
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Terug naar module
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg p-6 mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <BookOpen className="w-6 h-6" />
          <h1 className="text-2xl font-bold">{currentSection.heading}</h1>
        </div>
        <p className="opacity-90">Quiz - {totalQuestions} vragen</p>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>Voortgang</span>
          <span className="font-medium">Vraag {currentQuestionIndex + 1} van {totalQuestions}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all"
            style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Flashcard - Question */}
      <div className="bg-white border-2 border-gray-200 rounded-xl shadow-lg p-8 mb-8 min-h-[400px] flex flex-col">
        <div className="flex-1">
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              Vraag {currentQuestionIndex + 1}
            </span>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentQuestion.question}
            </h2>
          </div>

          <div className="space-y-3">
            {currentOptions.map((option) => (
              <button
                key={option.label}
                onClick={() => handleAnswerSelect(option.label)}
                className={`w-full text-left p-5 rounded-lg border-2 transition-all ${
                  selectedAnswers[currentQuestionIndex] === option.label
                    ? 'border-purple-500 bg-purple-50 shadow-md'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center font-bold text-lg ${
                    selectedAnswers[currentQuestionIndex] === option.label
                      ? 'border-purple-500 bg-purple-500 text-white'
                      : 'border-gray-300 text-gray-600'
                  }`}>
                    {option.label}
                  </div>
                  <span className="text-gray-900 font-medium">{option.text}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-colors ${
            currentQuestionIndex === 0
              ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
          }`}
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Vorige
        </button>

        <div className="text-sm text-gray-500">
          {!selectedAnswers[currentQuestionIndex] && 'Selecteer een antwoord om verder te gaan'}
        </div>

        <button
          onClick={handleNext}
          disabled={!selectedAnswers[currentQuestionIndex]}
          className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-colors ${
            !selectedAnswers[currentQuestionIndex]
              ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
              : isLastQuestion
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-purple-600 text-white hover:bg-purple-700'
          }`}
        >
          {isLastQuestion ? 'Bekijk resultaten' : 'Volgende'}
          {!isLastQuestion && <ArrowRight className="w-5 h-5 ml-2" />}
        </button>
      </div>
    </div>
  );
};

